import { Table, TableSorting } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { useTable } from "@/hooks/useTable";
import { MRT_ColumnDef } from "material-react-table";
import { useMemo, useState } from "react";
import { TGetCompanyOutputDto } from "@/api/services/main/company/dtos/get-company-list.dto";
import { Grid, Stack, Typography } from "@mui/material";
import { Button } from "@/components/elements/Button";
import { getDynamicRoute } from "@/utils/url";
import { AppRoutes } from "@/routes/config";
import { Link, useNavigate } from "react-router-dom";
import { IconPlus } from "@/assets/icons";
import { text } from "@/theme/colors";
import { convertDateFromIsoString } from "@/features/company/utiles/datetime";
import { TPagination } from "@/types/api";
import { formatPhone, truncateWithEllipsis } from "@/utils/string";
import { MAX_COMPANY_NAME_LENGTH } from "@/features/company/constants/length";
import { formatIdWithPrefix } from "@/utils/number";

type TCompanyTableProps = {
  rows: TGetCompanyOutputDto[];
  isLoading: boolean;
  pagination?: TPagination;
};

export const CompanyTable = ({ rows, isLoading, pagination }: TCompanyTableProps) => {
  const navigate = useNavigate();

  const truncateText = (value: string | null | undefined) => {
    if (!value) return EMPTY_STR.TEXT;
    return truncateWithEllipsis(value, MAX_COMPANY_NAME_LENGTH);
  };

  const columns = useMemo<MRT_ColumnDef<TGetCompanyOutputDto>[]>(
    () => [
       {
        id: "syncDate",
        header: "同期日",
        size: 106,
        Cell: ({ row }) => row.original.syncDate ? convertDateFromIsoString(row.original.syncDate) : "(手動登録)",
      },
      {
        id: "organizationId",
        header: "組織ID",
        size: 118,
        Cell: ({ row }) => formatIdWithPrefix(row.original?.id, "S"),
      },
      {
        header: "管理番号",
        accessorKey: "managementNumber",
        size: 116,
        Cell: ({ row }) => truncateText(row.original?.managementNumber),
      },
      {
        header: "所属機関名",
        accessorKey: "companyName",
        size: 135,
        Header: <TableSorting name="company.companyName">所属機関名</TableSorting>,
        Cell: ({ row }) => truncateText(row.original.companyName),
      },
      {
        id: "companySpecificIndustry1.companySpecificIndustry",
        header: "分野",
        size: 135,
        Cell: ({ row }) => {
          const names = [row.original.companySpecificIndustry1, row.original.companySpecificIndustry2, row.original?.companySpecificIndustry3]
            .filter((i) => i?.companySpecificIndustry)
            .map((i) => i!.companySpecificIndustry);
          return truncateText(names.join("、"));
        },
        Header: <TableSorting name="companySpecificIndustry1.companySpecificIndustry">分野</TableSorting>,
      },
      {
        header: "連絡担当者",
        accessorKey: "contactPersonName",
        size: 134,
        Header: <TableSorting name="company.contactPersonName">連絡担当者</TableSorting>,
        Cell: ({ row }) => truncateText(row.original.contactPersonName),
      },
      {
        header: "電話番号",
        accessorKey: "contactPersonPhone",
        size: 120,
        Header: <TableSorting name="company.contactPersonPhone">電話番号</TableSorting>,
        Cell: ({ row }) => (row.original?.contactPersonPhone ? formatPhone(row.original.contactPersonPhone) : EMPTY_STR.TEXT),
      },

      {
        id: "currentLearnerCount",
        header: "外国人数",
        size: 88,
        Cell: ({ row }) => row.original?.currentLearnerCount ?? 0,
      },
      {
        header: "登録日",
        size: 134,
        Cell: ({ row }) => convertDateFromIsoString(row.original.createdAt ?? ""),
        Header: <TableSorting name="company.createdAt">利用開始日</TableSorting>,
      },
    ],
    [],
  );

  const toEditRoute = (companyId: number) => {
    const route = getDynamicRoute(AppRoutes.companyDetail, { companyId: companyId });
    navigate(route);
  };

  const { table } = useTable({ columns, rows, onClickRow: (row) => toEditRoute(row.id), renderEmptyRowsFallback: ({}) => <span></span> });

  const formatTotal = (total: number = 0) => {
    return total.toLocaleString("en-US");
  };

  const page = pagination?.page ?? 0;
  const perPage = pagination?.perPage ?? 0;
  const count = pagination?.count ?? 20;

  const countItem = count + perPage * page - perPage;

  const [heightTable] = useState<string>("calc(100vh - 560px)");

  return (
    <>
      <Grid container justifyContent="center" alignItems="center">
        <Grid item xs={6}>
          <Stack direction="row" gap={2}>
            <Typography variant="body14Semi" color={text.primary}>
              全 {formatTotal(pagination?.total) ?? 0} 件中 {countItem ?? 0} 件を表示
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={6}>
          <Stack direction="row" justifyContent="end" sx={{ gap: "18px" }}>
            <Button size="sm" component={Link} to={AppRoutes.companyCreate} startIcon={<IconPlus width={20} height={20} />}>
              新規機関登録
            </Button>
          </Stack>
        </Grid>
      </Grid>
      <Table table={table} isLoading={isLoading} height={heightTable} minHeight={150} />
    </>
  );
};
